import React, { useMemo, useState } from "react";
import { DashboardFilters, DashboardFiltersBar } from "../../components/DashboardFiltersBar";
import { DataTable } from "../../components/DataTable";
import { useAuth } from "../../auth/AuthContext";
import { PageState } from "../../components/PageState";
import { usePagedQuery } from "../../hooks/usePagedQuery";

type SyncRun = {
  id: number;
  provider: string;
  status: string;
  startedAt: string;
  endedAt?: string | null;
  totals?: Record<string, number> | null;
  errorMessage?: string | null;
};

export const OpenFinanceSyncRunsPage: React.FC = () => {
  const { client } = useAuth();
  const [filters, setFilters] = useState<DashboardFilters>({});
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [message, setMessage] = useState<string | null>(null);

  const listFilters = useMemo(
    () => ({
      entityId: filters.entityId,
      from: filters.from,
      to: filters.to,
      provider: provider || undefined,
      status: status || undefined,
    }),
    [filters, provider, status]
  );

  const { rows, total, loading, error, page, take, setPage } =
    usePagedQuery<SyncRun>({
      endpoint: "/integrations/openfinance/sync-runs",
      filters: listFilters,
    });

  const handleReprocess = async () => {
    if (!filters.entityId) {
      setMessage("Selecione a entidade.");
      return;
    }
    const response = await client.post("/openfinance/reconciliation/reprocess", {
      entityId: filters.entityId,
      from: filters.from,
      to: filters.to,
      provider: provider || undefined,
    });
    if (!response.ok) {
      setMessage("Falha ao reprocessar.");
      return;
    }
    setMessage("Reprocessamento iniciado.");
  };

  return (
    <div className="container py-4">
      <h1 className="mb-3">Open Finance Sync Runs</h1>
      <DashboardFiltersBar value={filters} onChange={setFilters} />

      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-3">
              <label className="form-label">Provider</label>
              <select
                className="form-select"
                value={provider}
                onChange={(event) => setProvider(event.target.value)}
              >
                <option value="">Todos</option>
                <option value="MERCADO_PAGO">Mercado Pago</option>
                <option value="BTG">BTG</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Status</label>
              <select
                className="form-select"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">Todos</option>
                <option value="RUNNING">Running</option>
                <option value="SUCCESS">Success</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
            <div className="col-md-6 text-end">
              <button className="btn btn-outline-primary" onClick={handleReprocess}>
                Reprocessar por periodo
              </button>
            </div>
          </div>
          {message && <div className="alert alert-info mt-3">{message}</div>}
        </div>
      </div>

      <PageState loading={loading} error={error}>
        <DataTable<SyncRun>
          title="Execucoes de sincronizacao"
          rows={rows}
          total={total}
          page={page}
          take={take}
          onPageChange={setPage}
          columns={[
            { label: "Provider", render: (item) => item.provider },
            { label: "Status", render: (item) => item.status },
            { label: "Inicio", render: (item) => item.startedAt },
            { label: "Fim", render: (item) => item.endedAt ?? "-" },
            {
              label: "Totais",
              render: (item) =>
                item.totals
                  ? `importados ${item.totals.imported ?? 0}, novos ${item.totals.new ?? 0}, conciliados ${item.totals.conciliated ?? 0}, erros ${item.totals.errors ?? 0}`
                  : "-",
            },
            { label: "Erro", render: (item) => item.errorMessage ?? "-" },
          ]}
        />
      </PageState>
    </div>
  );
};
